<template>
  <PanelPage ref="useAlerts" :loading-overlay="loadingOverlay" md="12">
    <v-app-bar class="pt-1" elevation="1">
      <v-container>
        <v-row class="align-center flex-nowrap">
          <v-col cols="auto" class="align-self-center">
            <v-app-bar-title>{{ t('title') }}</v-app-bar-title>
          </v-col>
          <v-col>
            <v-autocomplete
              :label="t('park_to_modify')"
              :items="parks"
              v-model="selectedParkId"
              item-title="name"
              item-value="id"
              hide-details
              density="compact"
            />
          </v-col>
          <v-col cols="auto">
            <v-btn icon="mdi-refresh" border @click="refresh" :disabled="isAllDisabled"
                   :title="t('refresh_tooltip')"/>
          </v-col>
          <v-divider vertical/>
          <v-col cols="auto">
            <v-app-bar-title>{{ t('bonk') }}</v-app-bar-title>
          </v-col>
          <v-col cols="auto" class="pa-0">
            <v-tooltip :text="bonkStatus">
              <template v-slot:activator="{ props }">
                <svg v-bind="props" class="d-block" width="32" height="32" viewBox="0 0 32 32"
                     xmlns="http://www.w3.org/2000/svg">
                  <circle cx="16" cy="16" r="14" :fill="bonkColor" stroke="#000" stroke-linejoin="bevel"
                          stroke-width="4"/>
                </svg>
              </template>
            </v-tooltip>
          </v-col>
          <v-col cols="auto">
            <v-btn border @click="routeToBonk(selectedPark.name)" :disabled="isAllDisabled">{{
                t('open_in_new_tab')
              }}
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </v-app-bar>
    <v-row>
      <v-col>
        <Generals
          v-model:use-alerts="useAlerts"
          v-model:loading-overlay="loadingOverlay"
          :original-park="selectedPark"
          :agencys="agencys"
          @updated="refresh"
          :disabled="isAllDisabled"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-expansion-panels>
          <v-expansion-panel>
            <v-expansion-panel-title class="pa-0 pe-3">
              <v-card-title>
                {{ t('cs_details') }}
              </v-card-title>
            </v-expansion-panel-title>
            <!--suppress PointlessBooleanExpressionJS -->
            <v-expansion-panel-text v-if="selectedPark !== null">
              <v-row>
                <v-col cols="4"
                       v-for="cs in selectedPark.chargingstations.sort((a, b) => objComparatorNumber(a, b, 'park_bnum'))">
                  <ChargingStation
                    v-model:use-alerts="useAlerts"
                    v-model:loading-overlay="loadingOverlay"
                    :cs="cs"
                    :money-dests="moneyDests"
                    @updated="refresh"
                    class="fill-height"
                  />
                </v-col>
              </v-row>
            </v-expansion-panel-text>
            <v-expansion-panel-text v-else class="text-center">
              {{ t('select_park_first') }}
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <MaintenanceData
          v-model:use-alerts="useAlerts"
          v-model:loading-overlay="loadingOverlay"
          :original-park="selectedPark"
          @updated="refresh"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <Address
          v-model:use-alerts="useAlerts"
          v-model:loading-overlay="loadingOverlay"
          :original-park="selectedPark"
          @updated="refresh"
          :disabled="isAllDisabled"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <CustomMessage
          v-model:use-alerts="useAlerts"
          v-model:loading-overlay="loadingOverlay"
          :original-park="selectedPark"
          @updated="refresh"
          :disabled="isAllDisabled"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <EmailNotification
          v-model:use-alerts="useAlerts"
          v-model:loading-overlay="loadingOverlay"
          :original-park="selectedPark"
          @updated="refresh"
          :disabled="isAllDisabled"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <UserReports
          v-model:use-alerts="useAlerts"
          v-model:loading-overlay="loadingOverlay"
          :original-park="selectedPark"
          :park-images="parkImages"
          @updated="refresh"
          :disabled="isAllDisabled"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <Ipsum
          v-model:use-alerts="useAlerts"
          v-model:loading-overlay="loadingOverlay"
          :original-park="selectedPark"
          :ipsum-devices="ipsumDevices"
          @updated="refresh"
          :disabled="isAllDisabled"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <ApplyToAll
          v-model:use-alerts="useAlerts"
          v-model:loading-overlay="loadingOverlay"
          :original-park="selectedPark"
          :park-logos="parkLogos"
          :money-dests="moneyDests"
          @updated="refresh"
          :disabled="isAllDisabled"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <ClosureTable
          v-model:use-alerts="useAlerts"
          v-model:loading-overlay="loadingOverlay"
          :original-park="selectedPark"
          @updated="refresh"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <ResizePark
          v-model:use-alerts="useAlerts"
          v-model:loading-overlay="loadingOverlay"
          :original-park="selectedPark"
          @updated="refresh"
          :disabled="isAllDisabled"
        />
      </v-col>
    </v-row>
  </PanelPage>
</template>

<script setup>
// TODO: Convert the prop-passing logic to a STORE!!!!!
import { ref, onMounted, watch, computed, onUnmounted } from "vue";
import PanelPage from "../components/PanelPage.vue";
import Generals from "../components/ModifyPark/Generals.vue";
import Address from "../components/ModifyPark/Address.vue";
import ApplyToAll from "../components/ModifyPark/ApplyToAll.vue";
import UserReports from "../components/ModifyPark/UserReports.vue";
import ChargingStation from "../components/ModifyPark/ChargingStation.vue";
import { fetchParks, fetchParkById } from "../remote/park.js";
import { getBonknames, routeToBonk } from "../remote/bonk.js";
import { fetchIpsumDevices } from "../remote/ipsum.js";
import { fetchAgencys } from "../remote/agency.js";
import { endpoint } from "../remote/utils.js";
import { objComparatorNumber } from "../utils.js";
import axios from "axios";
import ResizePark from "../components/ModifyPark/ResizePark.vue";
import { fetchMoneyDests } from "../remote/moneydest.js";
import CustomMessage from "../components/ModifyPark/CustomMessage.vue";
import Ipsum from "../components/ModifyPark/Ipsum.vue";
import EmailNotification from "@/components/ModifyPark/EmailNotification.vue";
import MaintenanceData from "@/components/ModifyPark/MaintenanceData.vue";
import ClosureTable from "@/components/ModifyPark/ClosureTable.vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n({ inheritLocale: true, useScope: 'local' });

// Common State
const useAlerts = ref(null);
const loadingOverlay = ref(false);
const bonkCheckInterval = ref(null);

// State
const parks = ref([]);
const selectedParkId = ref(null);
const selectedPark = ref(null);
const isBonkOnline = ref(null);
const parkImages = ref([]);
const moneyDests = ref([]);
const parkLogos = ref([]);
const ipsumDevices = ref([]);
const agencys = ref([]);

const isAllDisabled = computed(() => {
  return selectedParkId.value === null;
});

const bonkColor = computed(() => {
  if (selectedParkId.value === null) {
    return '#808080';
  } else if (isBonkOnline.value === null) {
    return '#FACC00';
  } else if (isBonkOnline.value) {
    return '#00FF00';
  } else {
    return '#FF0000';
  }
});

const bonkStatus = computed(() => {
  if (selectedParkId.value === null) {
    return t('status_select_park');
  } else if (isBonkOnline.value === null) {
    return t('status_asking');
  } else if (isBonkOnline.value) {
    return t('status_online');
  } else {
    return t('status_offline');
  }
});

watch(selectedParkId, async (newValue) => {
  if (newValue !== null) {
    loadingOverlay.value = true;
    selectedPark.value = await fetchParkById(newValue);
    console.log(selectedPark.value);
    document.title = `MD ${selectedPark.value.name}`;
    loadingOverlay.value = false;
    await checkIfBonkOnline();
    bonkCheckInterval.value = setInterval(checkIfBonkOnline, 120000);
  } else {
    clearInterval(bonkCheckInterval.value);
    bonkCheckInterval.value = null;
  }
});

async function refresh() {
  if (selectedParkId.value !== null) {
    loadingOverlay.value = true;
    selectedPark.value = await fetchParkById(selectedParkId.value);
    console.log(selectedPark.value);
    document.title = `MD ${selectedPark.value.name}`;
    loadingOverlay.value = false;
  }
}

async function checkIfBonkOnline() {
  if (import.meta.env.MODE === 'development') return;
  if (selectedPark.value !== null) {
    const onlines = await getBonknames();
    isBonkOnline.value = onlines.includes(selectedPark.value.name);
  }
}

// TODO: Move all the media fetch inside a remote js file
async function fetchParkImages() {
  try {
    const res = await axios.get(endpoint('internal/current/parkimages/'), {
      withCredentials: true
    });
    return res.data.sort((a, b) => objComparatorNumber(a, b, 'id')).map(el => {
      return { ...el, visual_name: `${el['id']} - ${el['alias']}` };
    });
  } catch (e) {
    useAlerts.value.errorAlert(t('error_yearly_images'), e.toString());
  }
}

async function fetchParkLogos() {
  try {
    const res = await axios.get(endpoint('internal/current/mediaimages/'), {
      withCredentials: true
    });
    return res.data.sort((a, b) => objComparatorNumber(a, b, 'id')).map(el => {
      return { ...el, visual_name: `${el['id']} - ${el['alias']}` };
    });
  } catch (e) {
    useAlerts.value.errorAlert(t('error_park_logos'), e.toString());
  }
}

onMounted(async () => {
  document.title = t('title');
  loadingOverlay.value = true;
  try {
    [
      parks.value,
      parkImages.value,
      moneyDests.value,
      parkLogos.value,
      ipsumDevices.value,
      agencys.value,
    ] = await Promise.all([
      fetchParks(),
      fetchParkImages(),
      fetchMoneyDests(),
      fetchParkLogos(),
      fetchIpsumDevices(),
      fetchAgencys(),
    ]);
  } catch (e) {
    useAlerts.value.errorAlert(t('error_initial_infos'), e.toString());
  } finally {
    loadingOverlay.value = false;
  }
});

onUnmounted(() => {
  if (bonkCheckInterval.value !== null) clearInterval(bonkCheckInterval.value);
});

</script>

<i18n>
{
  "en": {
    "title": "Modify Park",
    "park_to_modify": "Park to modify",
    "refresh_tooltip": "Refresh selected park informations",
    "bonk": "Bonk",
    "open_in_new_tab": "Open in new tab",
    "cs_details": "Charging Station details",
    "select_park_first": "First select a park",
    "status_select_park": "Select a park first",
    "status_asking": "Asking the server...",
    "status_online": "Online",
    "status_offline": "Offline",
    "error_yearly_images": "Impossible to load Yearly Park Images",
    "error_park_logos": "Impossible to load Park Logos",
    "error_initial_infos": "Impossible to load initial infos"
  },
  "it": {
    "title": "Modifica Parco",
    "park_to_modify": "Parco da modificare",
    "refresh_tooltip": "Aggiorna le informazioni del parco selezionato",
    "bonk": "Bonk",
    "open_in_new_tab": "Apri in un nuovo pannello",
    "cs_details": "Dettagli Stazione di Ricarica",
    "select_park_first": "Seleziona prima un parco",
    "status_select_park": "Seleziona prima un parco",
    "status_asking": "Interrogando il server...",
    "status_online": "Online",
    "status_offline": "Offline",
    "error_yearly_images": "Impossibile caricare le immagini annuali del parco",
    "error_park_logos": "Impossibile caricare il logo del parco",
    "error_initial_infos": "Impossibile caricare le informazioni iniziali"
  },
  "fr": {
    "title": "Modifier le Parc",
    "park_to_modify": "Parc à modifier",
    "refresh_tooltip": "Mettre à jour les informations du parc sélectionné",
    "bonk": "Bonk",
    "open_in_new_tab": "Ouvrir dans un nouvel onglet",
    "cs_details": "Détails de la Station de Recharge",
    "select_park_first": "Sélectionnez d'abord un parc",
    "status_select_park": "Sélectionnez d'abord un parc",
    "status_asking": "Interrogation du serveur...",
    "status_online": "En ligne",
    "status_offline": "Hors ligne",
    "error_yearly_images": "Impossible de charger les images annuelles du parc",
    "error_park_logos": "Impossible de charger les logos du parc",
    "error_initial_infos": "Impossible de charger les informations initiales"
  }
}
</i18n>

<style scoped>

</style>
